"use client"

import React from "react"

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
  componentStack: string | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught render error", {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    })
    this.setState({ componentStack: info.componentStack ?? null })
  }

  private handleReset = () => {
    this.setState({ error: null, componentStack: null })
  }

  render() {
    const { error, componentStack } = this.state

    if (!error) {
      return this.props.children
    }

    if (this.props.fallback) {
      return this.props.fallback
    }

    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-4 rounded-lg border bg-card p-6 shadow-sm">
          <div>
            <h1 className="text-lg font-semibold">Bir hata oluştu</h1>
            <p className="text-sm text-muted-foreground">
              Sayfa yüklenirken beklenmeyen bir hata meydana geldi.
            </p>
          </div>
          <div className="space-y-2 rounded-md bg-muted/40 p-3 text-xs">
            <div className="font-mono break-all">{error.message}</div>
            {componentStack ? (
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-muted-foreground">
                {componentStack}
              </pre>
            ) : null}
          </div>
          <button
            type="button"
            onClick={this.handleReset}
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Tekrar dene
          </button>
        </div>
      </div>
    )
  }
}
